/**
 * Careline's tool contract, authored once in zod: each tool's name, title, input and output schema,
 * annotations and Talkie policy. build.mjs generates the `.well-known` manifest from `toolManifest()`
 * (the host validates every call's arguments and every `structuredContent` against that manifest), and
 * the handlers in `tools.ts` parse with these same schemas at run time, so Careline's bundle carries zod
 * on purpose (`self-test.json` `bundleMayCarry`). No DOM here: build.mjs runs this module in Node.
 */

import { z } from "zod/v4";
import { withPolicy } from "../../shared/policy.mjs";
import { APP, REQUESTED_CAPABILITIES } from "./app-identity";
import { orderFields, orderSchema, encounterSchema, noteSchema, resultSchema, persistenceSchema } from "./domain.js";
import { patientRow, chartFacts, visitRow, medicationRow, resultRow, encounterRef, patientRef } from "./rows.js";
import { noteReviewPayload, reviewConfirmPayload } from "./review.js";
export { APP, REQUESTED_CAPABILITIES };

const id = z.string().trim().min(1).max(200);
// Every argument says what it is: a model fills these knowing only the schema. An undescribed
// `request_id` was read as something to ask the clinician for (prod, 2026-10-01).
const patient = {
  patient_id: id.describe("The patient's ID (MRN), from list_schedule or search_patients, e.g. \"10428\"."),
};
const encounter = {
  ...patient,
  encounter_id: id.describe("The patient's current encounter ID, from list_schedule or get_chart_summary."),
};
const mutation = {
  ...encounter,
  expected_revision: z
    .number()
    .int()
    .positive()
    .describe(
      "The encounter's current revision, read from get_encounter just before this change. A stale revision is refused: read it again, then retry.",
    ),
  // The HOST mints this (policy `idempotency_key`, owner 2026-10-14): the model never sees the field and a value
  // it writes is dropped. The same key is reused when the model retries a step whose earlier attempt failed, so a
  // retry returns the first result instead of applying the change twice. The text is for people reading the manifest.
  request_id: id.describe(
    "Idempotency key for this one change, assigned by the host. Never ask the user for it. A retry of the same step reuses it and returns the first result instead of applying the change twice.",
  ),
};
const resultId = id.describe("A result ID, from get_results.");
// A patient has at most five results; the store refuses a repeated or unknown ID (`resultIndexes`).
const resultIds = z.array(resultId).min(1).max(5);
const orderId = id.describe("An order ID, from list_orders.");
const sections = [
  "overview",
  "history",
  "results",
  "medications",
  "background",
  "encounter",
  "orders",
  "summary",
] as const;

// Output shapes, one per tool, so the host's structured-output gate checks real content.
export const outputs = {
  patients: z.object({ patients: z.array(patientRow) }).strict(),
  chart: z
    .object({
      patient: chartFacts,
      latest_visit: visitRow,
      medications: z.array(medicationRow),
      encounter: encounterRef,
    })
    .strict(),
  visits: z.object({ visits: z.array(visitRow) }).strict(),
  medications: z
    .object({
      patient: patientRef,
      encounter: encounterRef,
      active: z.array(medicationRow),
      prescriptions: z.array(orderSchema),
      historical: z.array(
        z
          .object({ name: z.string(), strength: z.string(), directions: z.string(), date: z.string() })
          .strict(),
      ),
    })
    .strict(),
  results: z
    .object({
      patient: patientRef,
      encounter: encounterRef,
      results: z.array(resultRow),
      awaiting_results: z.array(orderSchema),
    })
    .strict(),
  result: z.object({ result: resultRow }).strict(),
  encounter: z.object({ encounter: encounterSchema }).strict(),
  orders: z.object({ orders: z.array(orderSchema), revision: z.number().int() }).strict(),
  orderables: z
    .object({
      catalogs: z.record(z.string(), z.array(z.string())),
      prescription: z.object({ entry: z.string(), pharmacies: z.array(z.string()) }).strict(),
    })
    .strict(),
  workflow: z
    .object({
      encounter_id: z.string(),
      revision: z.number().int(),
      signed: z.boolean(),
      blockers: z.array(z.string()),
    })
    .strict(),
  summary: z
    .object({
      final: z.boolean(),
      instructions: z.string(),
      followup: z.string(),
      diagnoses: z.array(z.string()),
      orders: z.array(orderSchema),
    })
    .strict(),
  shown: z.object({ patient_id: z.string(), section: z.string() }).strict(),
  review: z
    .object({
      patient: patientRef,
      patient_id: z.string(),
      encounter_id: z.string(),
      revision: z.number().int(),
      order: orderSchema,
    })
    .strict(),
  mutation: resultSchema,
  // The three tools that ask the clinician to decide answer one of two ways: a renderer host (it advertised the UI
  // extension) gets the exact data to decide on and nothing is written; any other host gets today's mutation result.
  // One declared schema admits both, since the host validates every result against it (FR-6).
  mutationOrNoteReview: z.union([resultSchema, noteReviewPayload]),
  mutationOrReviewConfirm: z.union([resultSchema, reviewConfirmPayload]),
};
const envelope = (data: z.ZodType) =>
  z.object({ simulated: z.literal(true), persistence: persistenceSchema, data }).strict();
type Output = keyof typeof outputs;
// `review`: the write runs only after the clinician confirms it in Careline (`presents_ui`); that card is its gate.
// `appOnly`: only the app's own view may call it (`_meta.ui.visibility: ["app"]`), so it never enters Dobby's inventory.
// `view`: the view a renderer host shows the result in (`_meta.ui.resourceUri`, own-app views FR-2). The trusted
// declaration is what binds it; a call result never names a view.
type Kind = { write?: boolean; sensitive?: boolean; destructive?: boolean; review?: boolean; appOnly?: boolean; view?: View };
/** Careline's own-app views by `ui://` resource URI; `views/index.ts` serves one document per URI. */
export const VIEWS = {
  noteReview: "ui://careline/note-review",
  reviewConfirm: "ui://careline/review-confirm",
  results: "ui://careline/results",
  medications: "ui://careline/medications",
  order: "ui://careline/order",
} as const;
type View = (typeof VIEWS)[keyof typeof VIEWS];
function tool(name: string, title: string, summary: string, description: string, shape: z.ZodRawShape, output: Output, kind: Kind = {}) {
  return {
    name,
    title,
    summary,
    description,
    schema: z.object(shape).strict(),
    output: envelope(outputs[output]),
    write: !!kind.write,
    // A tool that takes a `request_id` names it as its idempotency key; deriving it from the shape means a
    // write tool cannot forget (all 11 take `...mutation`).
    idempotencyKey: Object.hasOwn(shape, "request_id") ? "request_id" : undefined,
    sensitive: !!kind.sensitive,
    destructive: !!kind.destructive,
    review: !!kind.review,
    appOnly: !!kind.appOnly,
    view: kind.view,
  };
}
const write = { write: true };
export const tools = [
  tool("list_schedule", "List Schedule", "Lists today's fictional appointments.", "List the fictional schedule with stable patient and encounter IDs.", {}, "patients"),
  tool(
    "search_patients",
    "Search Patients",
    "Finds a fictional patient by name or MRN.",
    "Find fictional patients by name or MRN. Ask the user to choose ambiguous matches.",
    { query: z.string().max(200).describe("A patient name or MRN, or part of one.") },
    "patients",
  ),
  tool(
    "get_chart_summary",
    "Get Chart Summary",
    "Reads a patient's chart summary: allergies, medications and current visit.",
    "Read chart facts, latest visit, allergies, medication projection and current encounter.",
    patient,
    "chart",
  ),
  tool(
    "get_visit_history",
    "Get Visit History",
    "Reads a patient's earlier visits.",
    "Read prior fictional visits and the completed simulated encounter.",
    patient,
    "visits",
  ),
  tool(
    "get_medications",
    "Get Medications",
    "Reads a patient's active medications and prescription history.",
    "Read active medications and prescription history; unsigned drafts are not active.",
    patient,
    "medications",
    { view: VIEWS.medications },
  ),
  tool(
    "get_results",
    "Get Results",
    "Reads a patient's results and orders still awaiting results.",
    "Read final fictional results and diagnostic orders awaiting results.",
    patient,
    "results",
    { view: VIEWS.results },
  ),
  tool(
    "get_result",
    "Get Result",
    "Reads one result without marking it reviewed.",
    "Read one real fixture result. Reading does not mark it reviewed.",
    { ...patient, result_id: resultId },
    "result",
  ),
  tool("get_encounter", "Get Encounter", "Reads the current visit note and its revision.", "Read the encounter and current revision before editing.", encounter, "encounter"),
  tool("list_orders", "List Orders", "Lists the visit's draft and signed orders.", "Read draft and signed simulated orders with stable IDs.", encounter, "orders"),
  tool(
    "list_orderables",
    "List Order Options",
    "Lists the tests and referrals that can be ordered.",
    "List available diagnostic/referral choices. Prescriptions are clinician-entered.",
    {},
    "orderables",
  ),
  tool("get_workflow_status", "Get Workflow Status", "Reads what still blocks signing this visit.", "Read completion blockers and current revision.", encounter, "workflow"),
  tool(
    "get_after_visit_summary",
    "Get After-Visit Summary",
    "Reads the patient's after-visit instructions and follow-up.",
    "Read instructions, follow-up and orders; final only after encounter signing.",
    encounter,
    "summary",
  ),
  tool(
    "show_chart",
    "Show Chart",
    "Opens a section of a patient's chart in Careline.",
    "Show the specified patient and section without altering clinical records.",
    { ...patient, section: z.enum(sections).describe("Which part of the chart to show.") },
    "shown",
  ),
  tool(
    "update_note",
    "Update Note",
    "Opens a visit-note draft in Careline for you to review and save.",
    "Propose visit-note sections for the clinician to review. Does NOT write immediately: the draft opens in Careline and only what the clinician saves is written, edits included (their review IS the gate). Draft only from chart facts and the clinician's own words; leave out any section you cannot ground, and never invent symptoms, history, exam findings or patient statements. The clinician may discard the draft: then nothing was written, so do not retry it; ask what they want instead. Requires the latest encounter revision; invalidates saved status.",
    { ...mutation, note: noteSchema.partial().describe("The note sections to propose; sections left out keep their current text.") },
    "mutationOrNoteReview",
    // ⚖️ Owner, 2026-10-07: a real planner wrote HPI/exam text nobody gave it and signed, behind an approval that
    // showed only "Update Note". The draft is reviewed in Careline instead, as Linear Copilot's draft_issue is.
    { write: true, review: true, view: VIEWS.noteReview },
  ),
  tool("save_note", "Save Note", "Saves the visit note draft without signing it.", "Validate and save the current note draft. Does not sign.", mutation, "mutation", write),
  tool(
    "set_diagnoses",
    "Set Diagnoses",
    "Sets the diagnoses addressed at this visit.",
    "Select chart diagnoses addressed at this encounter.",
    {
      ...mutation,
      diagnoses: z
        .array(id)
        .max(20)
        .describe(
          "The problems addressed at this encounter, each exactly as get_chart_summary lists it. Replaces the current selection.",
        ),
    },
    "mutation",
    write,
  ),
  tool(
    "set_review_status",
    "Set Review Status",
    "Asks you in Careline to confirm, or undo, your review of a chart item.",
    "Ask the clinician to confirm in Careline that THEY reviewed medications, allergies or history with the patient (or to undo that). Does NOT record anything by itself: the confirmation card in Careline is the gate, and only the clinician's Confirm records it. Call it only when the clinician says they reviewed it; never call it because you read the chart. If they choose Not yet, nothing was recorded, so do not retry it; ask them instead.",
    {
      ...mutation,
      field: z
        .enum(["medReviewed", "allergyReviewed", "historyReviewed"])
        .describe("Which review to record: medications, allergies or history."),
      reviewed: z.boolean().describe("true asks the clinician to confirm they reviewed it; false asks them to undo that."),
    },
    "mutationOrReviewConfirm",
    // ⚖️ Owner, 2026-10-07: a real planner marked meds/allergies/history reviewed merely because it read them, behind
    // one trust approval. A review is the clinician's attestation, so Careline asks THEM (as for update_note).
    { write: true, review: true, view: VIEWS.reviewConfirm },
  ),
  tool(
    "review_result",
    "Mark Result Reviewed",
    "Asks you in Careline to confirm you reviewed one or more results.",
    "Ask the clinician to confirm in Careline that THEY reviewed patient results. Does NOT mark them by itself: the confirmation card in Careline is the gate, and only the clinician's Confirm marks them. Send every result they say they reviewed in ONE call: one card covers them all, and one call per result opens a card each. Call it only when the clinician says they reviewed them; never call it because you read a result. If they choose Not yet, nothing was marked, so do not retry it; ask them instead.",
    { ...mutation, result_ids: resultIds.describe("Every result the clinician says they reviewed, from get_results.") },
    "mutationOrReviewConfirm",
    // Same attestation class as set_review_status (careline-set-review-status-is-a-review): the clinician confirms.
    { write: true, review: true, view: VIEWS.reviewConfirm },
  ),
  // The two writes a view makes AFTER the clinician decides (own-app views FR-6). Both are app-only (Dobby never sees
  // them: the incidents behind update_note/review_result recur if a model can call a write that skips the review) and
  // both are sensitive, so Talkie asks again on every call (Q2, owner 2026-10-09) — a view click is never proof of intent.
  tool(
    "apply_note_draft",
    "Apply Note Draft",
    "Writes the visit note exactly as you reviewed and edited it.",
    "Called by Careline's note-review view when the clinician saves: write the COMPLETE note exactly as it was shown, edits included. Not callable by Dobby.",
    { ...mutation, note: noteSchema.describe("The complete note exactly as the clinician saw it, edits included.") },
    "mutation",
    { write: true, sensitive: true, appOnly: true },
  ),
  tool(
    "record_review",
    "Record Review",
    "Records your review of a chart item or result, as you confirmed it.",
    "Called by Careline's confirmation view when the clinician confirms: record exactly one attestation, either result_ids, or field with reviewed. Not callable by Dobby.",
    {
      ...mutation,
      result_ids: resultIds
        .optional()
        .describe("The results the clinician reviewed, recorded in one write. Send this, or field with reviewed, never both."),
      field: z
        .enum(["medReviewed", "allergyReviewed", "historyReviewed"])
        .optional()
        .describe("Which chart review to record: medications, allergies or history. Sent with reviewed."),
      reviewed: z.boolean().optional().describe("true records the clinician's review of field; false undoes it. Sent with field."),
    },
    "mutation",
    { write: true, sensitive: true, appOnly: true },
  ),
  tool(
    "create_order",
    "Create Order",
    "Drafts an unsigned order; nothing is sent.",
    "Create an unsigned diagnostic, referral or prescription draft. Never submits externally.",
    { ...mutation, order: orderFields.describe("The new order.") },
    "mutation",
    write,
  ),
  tool(
    "update_order",
    "Update Order",
    "Changes an unsigned order.",
    "Replace all fields of an unsigned order using the latest encounter revision.",
    { ...mutation, order_id: orderId, order: orderFields.describe("Every field of the order; replaces all of its current fields.") },
    "mutation",
    write,
  ),
  tool("remove_order", "Remove Order", "Removes an unsigned order.", "Remove an unsigned order only.", { ...mutation, order_id: orderId }, "mutation", {
    write: true,
    destructive: true,
  }),
  tool(
    "renew_prescription",
    "Renew Prescription",
    "Drafts an unsigned renewal of an active prescription.",
    "Create an unsigned renewal from an active medication, preserving exact renewal defaults.",
    { ...mutation, medication_id: id.describe("An active medication ID, from get_medications.") },
    "mutation",
    write,
  ),
  tool(
    "review_order",
    "Review Order",
    "Shows an order's exact content before signing.",
    "Return and show exact order content and the encounter revision to use when signing.",
    { ...encounter, order_id: orderId },
    "review",
    { view: VIEWS.order },
  ),
  tool(
    "sign_order",
    "Sign Order",
    "Signs the order and simulates sending it. Fictional; nothing real is sent.",
    "After fresh Talkie approval, sign the reviewed revision and simulate order submission or pharmacy delivery. No real delivery.",
    { ...mutation, order_id: orderId },
    "mutation",
    { write: true, sensitive: true },
  ),
  tool(
    "sign_encounter",
    "Sign Encounter",
    "Signs the visit once everything required is complete. Fictional.",
    "After fresh Talkie approval, sign the reviewed encounter revision if all prerequisites are complete. Simulated only.",
    mutation,
    "mutation",
    { write: true, sensitive: true },
  ),
];
// Each tool as the manifest declares it (schema v2): an MCP `Tool` with its policy under `_meta`. Inputs are
// described as the caller sends them (`io: 'input'`): a field with a default is optional to send.
// The Talkie policy rides `_meta["com.resatech.talkie/policy"]` (fixtures/shared/policy.mjs): every
// write needs the person's approval, and a sensitive one their fresh approval — except a `review` write, whose
// in-app card is the gate (`presents_ui`, owner's double-gate ruling for draft_issue): a second prompt would ask twice.
// MCP Apps' `_meta.ui`: the tool's view and who may call it; a tool that declares neither carries no `ui`.
const uiMeta = (t: (typeof tools)[number]) =>
  t.view || t.appOnly
    ? { ui: { ...(t.view && { resourceUri: t.view }), ...(t.appOnly && { visibility: ["app"] }) } }
    : undefined;
export const toolManifest = () =>
  tools.map((t) => ({
    name: t.name,
    title: t.title,
    description: t.description,
    inputSchema: z.toJSONSchema(t.schema, { io: "input" }) as { type: "object" },
    outputSchema: z.toJSONSchema(t.output) as { type: "object" },
    annotations: { readOnlyHint: !t.write, destructiveHint: t.destructive, idempotentHint: true, openWorldHint: false },
    _meta: withPolicy(uiMeta(t), {
      summary: t.summary,
      requires_approval: t.write && !t.review,
      sensitive: t.sensitive,
      presents_ui: t.review,
      idempotency_key: t.idempotencyKey,
    }),
  }));
